import React, { useEffect, useMemo, useRef, useState } from 'react';

// Demo component: simulates splitting a video into segments and adding overlays (image + captions)
export default function DemoEditPreview({ videoUrl }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [selectedId, setSelectedId] = useState('s1');
  const [duration, setDuration] = useState(30);
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });
  const [compact, setCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerHeight < 950 || window.innerWidth < 1400;
  });

  // Dummy initial segments (3 segments of 10s)
  const [segments, setSegments] = useState([
    {
      id: 's1', start: 0, end: 10,
      caption: 'In the beginning, a whisper becomes a world.',
      imageUrl: 'https://via.placeholder.com/240x120.png?text=Overlay+1',
      imagePos: { x: 65, y: 10, scale: 100 }, // percent positions
    },
    {
      id: 's2', start: 10, end: 20,
      caption: 'Characters gather like constellations in ink.',
      imageUrl: 'https://via.placeholder.com/200x200.png?text=Overlay+2',
      imagePos: { x: 5, y: 55, scale: 80 },
    },
    {
      id: 's3', start: 20, end: 30,
      caption: 'A turning point: courage finds its own voice.',
      imageUrl: 'https://via.placeholder.com/300x140.png?text=Overlay+3',
      imagePos: { x: 60, y: 55, scale: 90 },
    },
  ]);

  const selected = useMemo(() => segments.find(s => s.id === selectedId) || segments[0], [segments, selectedId]);
  const active = useMemo(() => segments.find(s => currentTime >= s.start && currentTime < s.end) || null, [segments, currentTime]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => setCurrentTime(v.currentTime || 0);
    const onLoaded = () => setDuration(v.duration || 30);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onLoaded);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onLoaded);
    };
  }, []);

  // Track viewport to toggle compact layout for 16" screens
  useEffect(() => {
    const onResize = () => {
      setCompact(window.innerHeight < 950 || window.innerWidth < 1400);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Track system theme changes
  useEffect(() => {
    const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    if (!mq) return;
    const handler = (e) => setIsDark(e.matches);
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.pause();
      setPlaying(false);
    } else {
      v.play().catch(() => {});
      setPlaying(true);
    }
  };

  const seekTo = (t) => {
    const v = videoRef.current; if (!v) return;
    v.currentTime = Math.max(0, Math.min(duration, t));
  };

  const onPickSegment = (id) => {
    setSelectedId(id);
    const seg = segments.find(s => s.id === id);
    if (seg) seekTo(seg.start);
  };

  const updateSelected = (patch) => {
    setSegments(prev => prev.map(s => s.id === selected.id ? { ...s, ...patch } : s));
  };

  const updateImagePos = (patch) => {
    setSegments(prev => prev.map(s => s.id === selected.id ? { ...s, imagePos: { ...s.imagePos, ...patch } } : s));
  };

  const simulateSplit = () => {
    // Rebuild 4 segments based on current duration (dummy)
    const segDur = Math.max(5, Math.floor(duration / 4));
    const next = Array.from({ length: 4 }).map((_, i) => ({
      id: `n${i+1}`,
      start: i * segDur,
      end: i === 3 ? duration : (i + 1) * segDur,
      caption: `Demo caption ${i + 1}`,
      imageUrl: `https://via.placeholder.com/${200+i*40}x${120+i*20}.png?text=Overlay+${i+1}`,
      imagePos: { x: 10 + i * 15, y: 10 + i * 10, scale: 80 + i * 5 },
    }));
    setSegments(next);
    setSelectedId(next[0].id);
    seekTo(0);
  };

  const pct = (n) => `${n}%`;
  const activeOverlay = active || selected; // prefer active segment while playing, else selected for editing

  // Theme-aware palette (inline)
  const colors = useMemo(() => (
    isDark ? {
      bg: '#0b0f14',
      surface: '#0f172a',
      surfaceAlt: '#111827',
      border: 'rgba(255,255,255,0.08)',
      text: '#e5e7eb',
      subtext: '#9aa0a6',
      accent: '#6c5ce7',
    } : {
      bg: '#f3f4f6',
      surface: '#ffffff',
      surfaceAlt: '#f9fafb',
      border: 'rgba(0,0,0,0.08)',
      text: '#111827',
      subtext: '#6b7280',
      accent: '#6c5ce7',
    }
  ), [isDark]);

  const btnStyle = {
    padding: compact ? '5px 8px' : '6px 10px',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    background: colors.surfaceAlt,
    color: colors.text,
    cursor: 'pointer',
    fontSize: compact ? 13 : 14,
  };

  const inputStyle = {
    padding: compact ? 5 : 8,
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    color: colors.text,
    fontSize: compact ? 13 : 14,
  };

  const labelStyle = { fontSize: compact ? 11 : 12, opacity: 0.8, color: colors.subtext };

  const containerMaxWidth = compact ? 960 : 1200;
  const previewMaxHeight = compact ? '34vh' : '48vh';
  const gridTemplate = compact ? '1fr' : '1fr 1fr';
  const headingStyle = { margin: 0, color: colors.text, fontSize: compact ? 15 : 17 };
  const headerMb = compact ? 6 : 10;
  const sectionMb = compact ? 6 : 8;
  const overlayPad = compact ? '8px 12px' : '12px 16px';
  const captionFs = compact ? 16 : 20;

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: compact ? 10 : 14, background: colors.surface, margin: '0 auto 24px', color: colors.text, maxWidth: containerMaxWidth }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: headerMb }}>
        <h3 style={headingStyle}>Editor Preview (Demo)</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={simulateSplit} style={btnStyle}>Simulate Split</button>
          <button onClick={togglePlay} style={btnStyle}>{playing ? 'Pause' : 'Play'}</button>
        </div>
      </div>

      {/* Preview area */}
  <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', background: `${isDark ? '#000' : '#fff'} url(${""}) center/cover no-repeat`, borderRadius: 8, overflow: 'hidden', marginBottom: sectionMb, border: `1px solid ${colors.border}`, maxHeight: previewMaxHeight }}>
        {/* Darken background for contrast */}
        <div style={{ position: 'absolute', inset: 0, background: isDark ? 'rgba(0,0,0,0.35)' : 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.0) 100%)' }} />
        <video ref={videoRef} src={videoUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} controls={false} />

        {/* Image overlay */}
        {activeOverlay?.imageUrl && (
          <img
            src={activeOverlay.imageUrl}
            alt="overlay"
            style={{
              position: 'absolute',
              left: pct(activeOverlay.imagePos.x),
              top: pct(activeOverlay.imagePos.y),
              transform: 'translate(-50%, -50%)',
              width: pct(activeOverlay.imagePos.scale),
              maxWidth: '60%',
              filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.5))'
            }}
          />
        )}

        {/* Caption overlay */}
        {activeOverlay?.caption && (
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: overlayPad, color: '#fff',
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.8) 100%)' }}>
    <div style={{ fontSize: captionFs, textAlign: 'center', textShadow: '0 2px 6px rgba(0,0,0,0.6)' }}>{activeOverlay.caption}</div>
          </div>
        )}
      </div>

      {/* Timeline segments */}
  <div style={{ display: 'flex', gap: 8, marginBottom: sectionMb, overflowX: 'auto' }}>
        {segments.map(s => (
          <div key={s.id} onClick={() => onPickSegment(s.id)}
    style={{ minWidth: compact ? 80 : 110, padding: compact ? 5 : 8, borderRadius: 6, cursor: 'pointer',
              border: `2px solid ${selectedId === s.id ? colors.accent : colors.border}`,
              background: selectedId === s.id ? 'rgba(108,92,231,0.12)' : colors.surfaceAlt, color: colors.text }}>
    <div style={{ fontWeight: 600, marginBottom: 4, color: colors.text, fontSize: compact ? 12 : 13 }}>Seg {s.id}</div>
    <div style={{ fontSize: compact ? 11 : 12, opacity: 0.8, color: colors.subtext }}>{s.start}s → {s.end}s</div>
          </div>
        ))}
      </div>

      {/* Editor controls for selected segment */}
      {selected && (
  <div style={{ display: 'grid', gridTemplateColumns: gridTemplate, gap: compact ? 10 : 12 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <label style={labelStyle}>Caption</label>
            <input value={selected.caption} onChange={(e) => updateSelected({ caption: e.target.value })}
              placeholder="Write caption…" style={inputStyle} />

            <label style={labelStyle}>Image URL</label>
            <input value={selected.imageUrl} onChange={(e) => updateSelected({ imageUrl: e.target.value })}
              placeholder="https://…" style={inputStyle} />

            <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr 1fr' : '1fr 1fr 1fr', gap: compact ? 8 : 10 }}>
              <div>
                <label style={labelStyle}>X (%)</label>
                <input type="number" value={selected.imagePos.x} onChange={(e) => updateImagePos({ x: Number(e.target.value) })}
                  min={0} max={100} step={1} style={{ ...inputStyle, width: '100%' }} />
              </div>
              <div>
                <label style={labelStyle}>Y (%)</label>
                <input type="number" value={selected.imagePos.y} onChange={(e) => updateImagePos({ y: Number(e.target.value) })}
                  min={0} max={100} step={1} style={{ ...inputStyle, width: '100%' }} />
              </div>
              {!compact && (
                <div>
                  <label style={labelStyle}>Scale (%)</label>
                  <input type="number" value={selected.imagePos.scale} onChange={(e) => updateImagePos({ scale: Number(e.target.value) })}
                    min={10} max={150} step={5} style={{ ...inputStyle, width: '100%' }} />
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={labelStyle}>Start (s)</label>
                <input type="number" value={selected.start} onChange={(e) => updateSelected({ start: Number(e.target.value) })}
                  min={0} max={duration} step={1} style={{ ...inputStyle, width: '100%' }} />
              </div>
              <div>
                <label style={labelStyle}>End (s)</label>
                <input type="number" value={selected.end} onChange={(e) => updateSelected({ end: Number(e.target.value) })}
                  min={0} max={duration} step={1} style={{ ...inputStyle, width: '100%' }} />
              </div>
            </div>
          </div>

          {/* Simple scrubber */}
          <div style={{ display: 'grid', gap: compact ? 6 : 8 }}>
            <label style={labelStyle}>Scrub (s)</label>
            <input type="range" min={0} max={Math.max(1, Math.floor(duration))} value={currentTime} onChange={(e) => seekTo(Number(e.target.value))}
              style={{ accentColor: colors.accent }} />
            <div style={{ fontSize: 13, opacity: 0.8, color: colors.subtext }}>t = {currentTime.toFixed(1)}s / {duration.toFixed(1)}s</div>

            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8, color: colors.subtext }}>
              This is a visual preview only. Actual processing (burn-in, rendering) would run on the backend.
            </div>

            <div style={{ marginTop: 'auto', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button disabled style={{ ...btnStyle, opacity: 0.7 }}>Export Preview</button>
              <button disabled style={{ ...btnStyle, background: colors.accent, border: 'none', opacity: 0.7 }}>Render (mock)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
